import { useEffect, useRef, useState } from "react";
import { emptyUserSession, UserSession } from "../model/UserSession";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

export const USER_SESSION_KEY = "_user_session_var_";

export enum AuthStatus {
  Idle = "IDLE",
  NewPasswordRequired = "NEW_PASSWORD_REQUIRED",
  LoggedIn = "LOGGED_IN",
}

export interface CurrentSessionData {
  userSessionState: UserSession;
  authStatus: AuthStatus;
  login: (username: string, password: string, onError?: (error: any) => void) => void;
  completeNewPassword: (newPassword: string, onError?: (error: any) => void) => void;
  logout: () => void;
}

export const useUserSession = (): CurrentSessionData => {
  const [userSessionState, setUserSessionState] =
    useState<UserSession>(emptyUserSession);
  const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.Idle);

  // The CognitoUser instance that received a NEW_PASSWORD_REQUIRED challenge must be
  // reused to complete it -- a fresh instance would not carry the challenge session.
  const challengeUserRef = useRef<CognitoUser | null>(null);
  const challengeAttributesRef = useRef<Record<string, string>>({});

  const storeSession = (userSession: UserSession) => {
    const json = JSON.stringify(userSession);
    setUserSessionState(userSession);
    window.sessionStorage.setItem(USER_SESSION_KEY, json);
  };

  const clearSession = () => storeSession(emptyUserSession);

  const storeAuthenticatedSession = (session: CognitoUserSession, fullName: string) => {
    storeSession({
      isLoggedIn: true,
      fullName,
      idToken: session.getIdToken().getJwtToken(),
    });
    setAuthStatus(AuthStatus.LoggedIn);
  };

  useEffect(() => {
    const json = window.sessionStorage.getItem(USER_SESSION_KEY);
    const userSession: UserSession = json ? JSON.parse(json) : emptyUserSession;
    setUserSessionState(userSession);
    if (userSession.isLoggedIn) {
      setAuthStatus(AuthStatus.LoggedIn);
    }
  }, []);

  const login = (username: string, password: string, onError?: (error: any) => void) => {
    const userPoolId = process.env.REACT_APP_USER_POOL_ID!;
    const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID!;

    const userPool = new CognitoUserPool({
      UserPoolId: userPoolId,
      ClientId: clientId,
    });

    const user = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    user.authenticateUser(authDetails, {
      onSuccess: (result) => {
        // Get user attributes after successful authentication
        user.getUserAttributes((err, attributes) => {
          if (err) {
            console.error("Error getting user attributes:", err);
            if (onError) {
              onError(err);
            }
            return;
          }

          // Find email attribute
          const emailAttr = attributes?.find(attr => attr.Name === 'email');
          storeAuthenticatedSession(result, emailAttr?.Value || username);
        });
      },
      onFailure: (err) => {
        if (onError) {
          onError(err);
        }
      },
      newPasswordRequired: (userAttributes) => {
        // Cognito rejects these read-only attributes if echoed back on the challenge.
        delete userAttributes.email;
        delete userAttributes.email_verified;

        // Hold on to the challenge so completeNewPassword() can resume it.
        challengeUserRef.current = user;
        challengeAttributesRef.current = userAttributes;
        setAuthStatus(AuthStatus.NewPasswordRequired);
      },
    });
  };

  const completeNewPassword = (newPassword: string, onError?: (error: any) => void) => {
    const user = challengeUserRef.current;
    if (!user) {
      if (onError) {
        onError(new Error("No hay una sesión de cambio de contraseña activa"));
      }
      return;
    }

    user.completeNewPasswordChallenge(newPassword, challengeAttributesRef.current, {
      onSuccess: (result) => {
        user.getUserAttributes((err, attributes) => {
          const emailAttr = attributes?.find(attr => attr.Name === 'email');
          storeAuthenticatedSession(result, emailAttr?.Value || user.getUsername());
          challengeUserRef.current = null;
          challengeAttributesRef.current = {};
        });
      },
      onFailure: (err) => {
        if (onError) {
          onError(err);
        }
      },
    });
  };

  const logout = () => {
    challengeUserRef.current = null;
    challengeAttributesRef.current = {};
    setAuthStatus(AuthStatus.Idle);
    clearSession();
  };

  return {
    userSessionState,
    authStatus,
    login,
    completeNewPassword,
    logout,
  };
};
